// Branding: the WMS logo (sign-in page + header) and the browser-tab favicon.
// Admin-managed, stored server-side in app_settings['branding'].
//
// Both are cached in localStorage and applied synchronously on load, so the mark is
// already correct on first paint instead of popping in when the API answers. The cache
// deliberately SURVIVES logout -- the sign-in page is exactly where a stale-but-right
// logo beats a placeholder. A fresh browser has no cache, which is why the fetch below
// hits /api/public-branding (no token required) rather than /api/branding.
// (Claude, 2026-08-07)

import { useEffect, useState } from 'react';

export const LOGO_KEY = 'wms_logo';
export const FAVICON_KEY = 'wms_favicon';
export const BRANDING_EVENT = 'wms-branding-changed';

// Shipped with the build, generated from the logo -- used whenever no custom favicon is set.
const DEFAULT_FAVICON = '/favicon.ico?v=2';

export interface Branding {
  wms_logo?: string | null;
  wms_favicon?: string | null;
}

export const cachedLogo = () => localStorage.getItem(LOGO_KEY);
export const cachedFavicon = () => localStorage.getItem(FAVICON_KEY);

/** Point the tab icon at `dataUrl`, or back at the bundled default when null. */
export function applyFavicon(dataUrl: string | null) {
  // Remove every existing icon link and append one. Browsers pick the LAST matching
  // link, so leaving the build's own <link rel="icon"> in place would make the result
  // depend on document order; mutating .href in place is also unreliably picked up.
  document.querySelectorAll('link[rel~="icon"]').forEach(n => n.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = dataUrl || DEFAULT_FAVICON;
  document.head.appendChild(link);
}

/** Write branding to the cache, apply the favicon, and notify mounted components. */
export function storeBranding(b: Branding) {
  const put = (key: string, val: string | null | undefined) => {
    if (val) localStorage.setItem(key, val);
    else localStorage.removeItem(key);
  };
  // Only touch keys the caller actually sent, so a partial save can't clear the other one.
  if ('wms_logo' in b) put(LOGO_KEY, b.wms_logo);
  if ('wms_favicon' in b) put(FAVICON_KEY, b.wms_favicon);
  applyFavicon(cachedFavicon());
  window.dispatchEvent(new CustomEvent(BRANDING_EVENT));
}

/** Refresh branding from the server. Safe to call before login. */
export async function loadPublicBranding(api: string) {
  try {
    const r = await fetch(`${api}/public-branding`);
    if (r.ok) storeBranding(await r.json());
  } catch { /* offline: keep whatever is cached */ }
}

/** The current logo, re-rendering whenever an admin changes it. */
export function useBrandingLogo(): string | null {
  const [logo, setLogo] = useState<string | null>(cachedLogo);
  useEffect(() => {
    const onChange = () => setLogo(cachedLogo());
    window.addEventListener(BRANDING_EVENT, onChange);
    return () => window.removeEventListener(BRANDING_EVENT, onChange);
  }, []);
  return logo;
}

/**
 * Redraw any image as a square PNG data URL, letterboxed to keep its aspect ratio and
 * its transparency. Used for the favicon: it normalises whatever an admin uploads to
 * one size and keeps the stored string small, since this one rides in localStorage and
 * is injected into every page load.
 */
export function toSquarePng(src: string, size = 128): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas is unavailable in this browser')); return; }
      const scale = Math.min(size / img.width, size / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Could not read that image'));
    img.src = src;
  });
}
